
import { useState } from "react";
import { format } from "date-fns";
import { Tables } from "@/integrations/supabase/types";
import { Button } from "@/components/ui/button";
import { Trash2, Pencil } from "lucide-react";
import { countryCodeToName, getCountryFlagUrl, getEventColor } from "@/utils/countryUtils";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { EditBusyPeriodDialog } from "./EditBusyPeriodDialog";

type BusyPeriod = Tables<"busy_periods">;

interface BusyPeriodsListProps {
  busyPeriods: BusyPeriod[] | undefined;
  handleDelete: (id: string) => Promise<void>;
}

export const BusyPeriodsList = ({ busyPeriods, handleDelete }: BusyPeriodsListProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingPeriod, setEditingPeriod] = useState<BusyPeriod | null>(null);

  if (!busyPeriods) {
    return <div>Loading busy periods...</div>;
  }

  return (
    <>
      <div className="space-y-2">
        {busyPeriods.map((period) => {
          const color = getEventColor(period.country || period.id);
          return (
          <TooltipProvider key={period.id}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div 
                  className="flex items-center justify-between p-3 rounded-lg border-l-4 hover:shadow-md transition-all group"
                  style={{ backgroundColor: color.bg, borderLeftColor: color.solid }}
                >
                  <div className="flex items-center gap-4">
                    {period.country && (
                      <div className="h-12 w-12 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center shrink-0">
                        <img
                          src={getCountryFlagUrl(period.country)}
                          alt={countryCodeToName(period.country)}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    )}
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">
                          {format(new Date(period.start_date), "MMM d, yyyy")} -{" "}
                          {format(new Date(period.end_date), "MMM d, yyyy")}
                        </p>
                        <Badge variant={period.team_scope === 'sales' ? 'default' : 'secondary'} className="capitalize">
                          {period.team_scope}
                        </Badge>
                      </div>
                      {period.country && (
                        <p className="text-sm text-muted-foreground">{countryCodeToName(period.country)}</p>
                      )}
                      <p className="text-sm text-muted-foreground">{period.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditingPeriod(period)}
                    >
                      <Pencil className="h-4 w-4 text-muted-foreground" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(period.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{period.description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          );
        })}
      </div>

      <EditBusyPeriodDialog
        period={editingPeriod}
        open={!!editingPeriod}
        onOpenChange={(open) => { if (!open) setEditingPeriod(null); }}
      />
    </>
  );
}
